/**
 * Venn Diagram Cards Section — GSAP autoplay timeline (frontend only).
 * Plays once on enter; keeps the final state while visible; resets only
 * when the section is fully below the viewport. Progress is time-based
 * (not scrubbed). No ScrollTrigger pin. Scoped per block instance.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

const BLOCK_SELECTOR =
	'.wp-block-alteia-venn-diagram-cards-section.alteia-venn-diagram-cards-section';
const DESKTOP_BREAKPOINT = 768;
const HEADER_SELECTOR = '.wp-block-alteia-header.alteia-header, .alteia-header';

/** Scale all timeline timing to ~60% of the original duration (~40% faster). */
const TIME_SCALE = 0.6;

let headerResizeObserver = null;
const activeSections = new Set();

/**
 * Whether the user prefers reduced motion.
 *
 * @return {boolean}
 */
function prefersReducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/**
 * Read the live site header height from the theme CSS variable or DOM.
 *
 * @return {number}
 */
function getHeaderHeight() {
	const cssValue = getComputedStyle( document.body )
		.getPropertyValue( '--alteia-header-height' )
		.trim();

	if ( cssValue ) {
		const parsed = parseFloat( cssValue );
		if ( ! Number.isNaN( parsed ) && parsed > 0 ) {
			return parsed;
		}
	}

	const header = document.querySelector( HEADER_SELECTOR );
	return header?.offsetHeight || 0;
}

/**
 * Sync sticky offset custom property on a section instance.
 *
 * @param {HTMLElement} section Section root element.
 */
function setStickyOffset( section ) {
	section.style.setProperty(
		'--alteia-venn-diagram-sticky-top',
		`${ getHeaderHeight() }px`
	);
}

/**
 * Refresh sticky offsets for all active sections and ScrollTrigger.
 */
function refreshHeaderOffsets() {
	activeSections.forEach( setStickyOffset );
	ScrollTrigger.refresh();
}

/**
 * Observe header height changes once for all block instances.
 */
function initHeaderOffsetObserver() {
	if ( headerResizeObserver ) {
		return;
	}

	const header = document.querySelector( HEADER_SELECTOR );

	if ( header && typeof ResizeObserver !== 'undefined' ) {
		headerResizeObserver = new ResizeObserver( () => {
			window.requestAnimationFrame( refreshHeaderOffsets );
		} );
		headerResizeObserver.observe( header );
	}

	let resizeTimer;
	window.addEventListener( 'resize', () => {
		window.clearTimeout( resizeTimer );
		resizeTimer = window.setTimeout( refreshHeaderOffsets, 150 );
	} );
}

/**
 * Show or hide the post-animation footer without touching the GSAP timeline.
 *
 * @param {HTMLElement} section   Section root element.
 * @param {boolean}     isVisible Whether the footer should be visible.
 */
function setFooterVisibility( section, isVisible ) {
	section.classList.toggle( 'is-footer-visible', Boolean( isVisible ) );
}

/**
 * Apply the final visible state without animation.
 *
 * @param {HTMLElement} section Section root element.
 */
function applyStaticState( section ) {
	section.classList.add( 'is-static' );
	setFooterVisibility( section, true );

	const contentWrapper = section.querySelector( '.diagram-content-wrapper' );
	const circleBgs = section.querySelectorAll( '.diagram-img-bg-color' );

	if ( contentWrapper ) {
		gsap.set( contentWrapper, { opacity: 1, y: 0 } );
	}

	if ( circleBgs.length ) {
		gsap.set( circleBgs, { opacity: 1 } );
	}
}

/**
 * Build movement values for circle animation.
 *
 * @param {NodeListOf<Element>|Element[]} sources   Elements whose centers are measured as origins.
 * @param {NodeListOf<Element>|Element[]} targets   Destination elements for each source.
 * @return {{ moveX: number[], moveY: number[], sX: number[], sY: number[] }}
 */
function getMovementValues( sources, targets ) {
	const moveX = [];
	const moveY = [];
	const sX = [];
	const sY = [];

	sources.forEach( ( source, i ) => {
		const target = targets[ i ];

		if ( ! source || ! target ) {
			moveX[ i ] = 0;
			moveY[ i ] = 0;
			sX[ i ] = 1;
			sY[ i ] = 1;
			return;
		}

		const t = target.getBoundingClientRect();
		const r = source.getBoundingClientRect();

		moveX[ i ] = t.left + t.width / 2 - ( r.left + r.width / 2 );
		moveY[ i ] = t.top + t.height / 2 - ( r.top + r.height / 2 );
		sX[ i ] = r.width ? t.width / r.width : 1;
		sY[ i ] = r.height ? t.height / r.height : 1;
	} );

	return { moveX, moveY, sX, sY };
}

/**
 * Initialize scroll animation for one section instance.
 *
 * @param {HTMLElement} section Section root element.
 */
function initVennDiagramSection( section ) {
	if ( section.dataset.vennInitialized === 'true' ) {
		return;
	}

	section.dataset.vennInitialized = 'true';
	activeSections.add( section );
	setStickyOffset( section );
	initHeaderOffsetObserver();

	if ( prefersReducedMotion() ) {
		section.classList.add( 'is-reduced-motion' );
		applyStaticState( section );
		return;
	}

	const diagramImgWrappers = section.querySelectorAll(
		'.diagram-img-wrapper'
	);
	const diagramImgContent2s = section.querySelectorAll(
		'.diagram-content-item .diagram-img-content'
	);
	const diagramImgBgColors = section.querySelectorAll(
		'.diagram-img-bg-color'
	);
	// Top-row circle contents only (exclude card-column placeholders).
	const diagramImgContents = section.querySelectorAll(
		'.diagram-wrapper:not(.diagram-content-wrapper) .diagram-img-content'
	);
	const diagramContentWrapper = section.querySelector(
		'.diagram-content-wrapper'
	);

	if ( ! diagramImgContents.length || ! diagramContentWrapper ) {
		applyStaticState( section );
		return;
	}

	// Keep CSS sticky disabled so scroll stays natural (no pin / no sticky fight).
	section.classList.add( 'is-autoplay-pin' );

	const ctx = gsap.context( () => {
		const mm = gsap.matchMedia();

		mm.add(
			{
				isDesktop: `(min-width: ${ DESKTOP_BREAKPOINT }px)`,
				isMobile: `(max-width: ${ DESKTOP_BREAKPOINT - 1 }px)`,
			},
			( context ) => {
				const { isDesktop } = context.conditions;
				let timeline = null;
				let playTrigger = null;
				let resetTrigger = null;
				let hasPlayed = false;

				const animatedElements = isDesktop
					? diagramImgContents
					: diagramImgWrappers;

				/**
				 * Restores the CSS-equivalent centered resting transform.
				 * Uses xPercent so GSAP does not wipe translateX(-50%).
				 */
				const setInitialTransforms = () => {
					gsap.set( animatedElements, {
						xPercent: -50,
						x: 0,
						y: 0,
						scaleX: 1,
						scaleY: 1,
					} );
					gsap.set( diagramContentWrapper, {
						clearProps: 'opacity,transform',
					} );
					gsap.set( diagramImgBgColors, { opacity: 0.2 } );
					setFooterVisibility( section, false );
				};

				const syncFooterToProgress = () => {
					setFooterVisibility(
						section,
						Boolean( timeline && timeline.progress() >= 1 )
					);
				};

				/**
				 * Instant off-screen reset — never reverse while visible.
				 */
				const resetTimeline = () => {
					if ( ! timeline ) {
						return;
					}

					timeline.pause( 0 );
					hasPlayed = false;
					setInitialTransforms();
				};

				const playTimeline = () => {
					if ( ! timeline || hasPlayed ) {
						return;
					}

					hasPlayed = true;
					timeline.restart();
				};

				const buildTimeline = () => {
					setStickyOffset( section );
					setInitialTransforms();

					const sources = isDesktop
						? diagramImgContents
						: diagramImgWrappers;
					const targets = isDesktop
						? diagramImgWrappers
						: diagramImgContent2s;

					const { moveX, moveY, sX, sY } = getMovementValues(
						sources,
						targets
					);

					timeline = gsap.timeline( {
						paused: true,
						onUpdate: syncFooterToProgress,
						onComplete: syncFooterToProgress,
					} );

					timeline.to( animatedElements, {
						xPercent: -50,
						x: ( i ) => moveX[ i ],
						y: ( i ) => moveY[ i ],
						scaleX: ( i ) => sX[ i ],
						scaleY: ( i ) => sY[ i ],
						duration: 2 * TIME_SCALE,
						delay: 0.2 * TIME_SCALE,
					} );

					timeline.to( diagramImgBgColors, {
						opacity: 1,
						duration: 1 * TIME_SCALE,
					} );

					if ( isDesktop ) {
						timeline.to( diagramContentWrapper, {
							y: 0,
							opacity: 1,
							duration: 1.5 * TIME_SCALE,
						} );
					} else {
						timeline.to( diagramContentWrapper, {
							opacity: 1,
							duration: 1.5 * TIME_SCALE,
						} );
					}

					// Play once when the section reaches the existing trigger point.
					playTrigger = ScrollTrigger.create( {
						trigger: section,
						start: () => `top top+=${ getHeaderHeight() }`,
						invalidateOnRefresh: true,
						onEnter: playTimeline,
					} );

					// Reset only once the whole section is fully below the viewport
					// (user scrolled back up past it). Never reverse while visible.
					resetTrigger = ScrollTrigger.create( {
						trigger: section,
						start: 'top bottom',
						invalidateOnRefresh: true,
						onLeaveBack: resetTimeline,
					} );

					// If the page loads already past this section, show the final state.
					if ( playTrigger.scroll() > playTrigger.start ) {
						hasPlayed = true;
						timeline.progress( 1 );
						syncFooterToProgress();
					}
				};

				window.requestAnimationFrame( () => {
					buildTimeline();
				} );

				return () => {
					playTrigger?.kill();
					resetTrigger?.kill();
					timeline?.kill();
					gsap.set( animatedElements, { clearProps: 'transform' } );
					gsap.set( diagramContentWrapper, {
						clearProps: 'opacity,transform',
					} );
					gsap.set( diagramImgBgColors, { clearProps: 'opacity' } );
					setFooterVisibility( section, false );
					hasPlayed = false;
				};
			}
		);

		section._vennMatchMediaRevert = () => mm.revert();
	}, section );

	section._vennGsapContext = ctx;
}

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( BLOCK_SELECTOR )
		.forEach( initVennDiagramSection );

	window.addEventListener( 'load', () => {
		window.requestAnimationFrame( refreshHeaderOffsets );
	} );
} );
